import React, { useRef, useState, useEffect } from 'react';

export interface SuggestionType {
  description: string;
  placeId: string;
  matchedIndex: number | undefined;
}

export const useGooglePlaces = (countryCode?: string, isGoogleMapsApiLoaded?: boolean) => {
  const [suggestions, setSuggestions] = useState<SuggestionType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);

  useEffect(() => {
    // Initialize Google Maps AutocompleteService
    autocompleteService.current = new google.maps.places.AutocompleteService();
  }, []);

  const fetchSuggestions = async (input: string) => {
    if (!autocompleteService.current) {
      setError('Google Maps API AutocompleteService is not initialized.');
      return;
    }

    if (!input) {
      // If there is no searchTerm return an empty array
      setSuggestions([]);
    }

    // Call Google Maps Autocomplete API
    autocompleteService.current.getPlacePredictions(
      { input, componentRestrictions: { country: countryCode || 'ca' } }, // You can adjust the country restriction
      (predictions, status) => {
        if (status === 'OK') {
          setSuggestions(
            predictions?.map((prediction) => {
              return {
                placeId: prediction.place_id,
                description: prediction.description,
                matchedIndex: prediction?.matched_substrings[0]?.length || undefined,
              };
            }) || [],
          );
        } else if (status === 'ZERO_RESULTS') {
          setSuggestions([]);
          setError('No results found.');
        } else {
          setError(`Error fetching suggestions: ${status}`);
        }
      },
    );
  };

  React.useEffect(() => {
    if (isGoogleMapsApiLoaded && !autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
    }
  }, [isGoogleMapsApiLoaded]);

  return { suggestions, fetchSuggestions, error, setError };
};
